import { string } from 'yup';
import type { TestContext } from 'yup';

import type { zoomError } from '@lib/zoom/types';
import { ZOOM_ERROR_CODES } from '@lib/zoom/error-codes';
import { ZOOM_ERROR_CODES_AUTHORIZED } from '@lib/zoom/error-codes-authorized';

import { logToCloudWatch } from '@services/logger.service';
import { ZOOM_PROXY_AUTH_PATH, applyAuthorizeParams } from '@lib/platforms';
import { ZOOM_JOIN_MEETING_URL_REGEX } from '@services/platform-service';
import {
  ZOOM_VALIDATION_ERROR,
  ZOOM_API_ERROR,
  ZOOM_API_CUSTOM_ERROR,
  ZOOM_MEETING_SDK_WEB_ERROR,
  EMPTY_LINK_ERROR_CODE,
  INVALID_LINK_ERROR_CODE,
  REGISTRATION_LINK_ERROR_CODE,
  EVENT_LINK_ERROR_CODE
} from '@lib/zoom/constants';

declare global {
  interface Window {
    zoomIntervalId: number | null;
    zoomError: any;
  }
}

export const resetZoomGlobals = () => {
  if (window.zoomIntervalId) {
    clearInterval(window.zoomIntervalId);
    window.zoomIntervalId = null;
  }
  window.zoomError = null;
};

export const resetZoomError = () => {
  window.zoomError = null;
};

// The full app path (pathname + query) a Zoom session was created from. Stored on
// the meeting as `data.meta.origin` at create time and echoed back on join errors
// (as `originPath`) so the UI can route the user back to where they started.
export const getSessionOrigin = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  return `${window.location.pathname}${window.location.search}`;
};

// Query flag added to a stored origin path when routing back after a join error,
// telling the destination page to re-open that platform's connect form (the
// failure itself is shown via the streamError toast). Value is the platform
// source key, e.g. 'zoom'.
export const REOPEN_PLATFORM_QUERY = 'reopenPlatform';

// Query flag carrying the failed meeting's id (zoom_user_meeting id) back to the
// origin page, so the re-opened connect form can reload that meeting's details
// (title/link/time) instead of coming up blank.
export const REOPEN_ZUMI_QUERY = 'zumi';

export const buildOriginRedirectUrl = (
  originPath: string,
  platform?: string,
  zumi?: string
): string => {
  let url = originPath;
  const append = (key: string, value: string) => {
    const separator = url.includes('?') ? '&' : '?';
    url = `${url}${separator}${key}=${encodeURIComponent(value)}`;
  };
  if (platform) append(REOPEN_PLATFORM_QUERY, platform);
  if (zumi) append(REOPEN_ZUMI_QUERY, zumi);
  return url;
};

export const isInBreakoutRoom = (bid: string | number) => {
  if (typeof bid === 'string' && bid.length > 0) {
    return true;
  }
  if (typeof bid === 'number') {
    return true;
  }
  return false;
};

export default resetZoomGlobals;

const getErrorKey = (error: zoomError) => {
  if (!error.origin) return null;
  if ([ZOOM_MEETING_SDK_WEB_ERROR, ZOOM_VALIDATION_ERROR, ZOOM_API_CUSTOM_ERROR].includes(error.origin)) {
    return error.errorCode;
  }

  if ([ZOOM_API_ERROR].includes(error.origin)) {
    return error.status;
  }

  return null;
};

export const getCustomZoomError = (error: zoomError, isAuthorized = false) => {
  try {
    let tmpError = { ...error, source: 'zoom', isCustom: false };

    const key = getErrorKey(error);
    tmpError.originalError = error;

    if (tmpError.reason === 'dependent assets are not accessible') {
      tmpError.message = 'Please disable Ad blocking extensions and try again!';
    }

    const ERROR_CODES = { ...ZOOM_ERROR_CODES, ...(isAuthorized ? ZOOM_ERROR_CODES_AUTHORIZED : {}) };
    if (key && ERROR_CODES[key]) {
      const customError = ERROR_CODES[key];
      return { ...tmpError, ...customError, reason: tmpError.reason || customError.reason, isCustom: true };
    }
    if ((!tmpError.message || tmpError.message === '') && tmpError?.reason) {
      tmpError.message = tmpError.reason;
    }
    tmpError.message = `Zoom reported a connection error: ${tmpError.message || 'Unknown error'}`;
    return tmpError;
  } catch (e) {
    logToCloudWatch('Error in getCustomZoomError', { error }, 'ERROR');
  }
};

export interface parseUrlReturnType {
  joinUrl?: string;
  urlType?: string;
  meetingId?: string;
  pwd?: string;
  tk?: string;
  zak?: string;
}

export const zoomLinkParser = (url: string) => {
  let joinUrl;
  let urlType;
  let meetingId;
  let pwd;
  let tk;
  let zak;
  let matches = url.match(ZOOM_JOIN_MEETING_URL_REGEX);

  if (!matches || matches.length < 3) {
    return { urlType, meetingId, pwd, tk, zak };
  }

  joinUrl = matches[0];
  urlType = matches[1];
  meetingId = matches[2];

  if (matches[3]) {
    const queryParams = new URLSearchParams(matches[3]);
    pwd = queryParams.get('pwd') || undefined;
    tk = queryParams.get('tk') || undefined;
    zak = queryParams.get('zak') || undefined;
  }

  return { joinUrl, urlType, meetingId, pwd, tk, zak };
};

export const parseErrorMessage = (error: string | object) => {
  try {
    if (error && typeof error === 'object') {
      return error;
    }
    const parsedError = JSON.parse(error);
    if (parsedError && typeof parsedError === 'object') {
      return parsedError;
    }
  } catch (e) {
    return {
      message: error,
      type: 'UNKNOWN_ERROR'
    };
  }
};

// Whether a Zoom failure is the meeting link's fault (wrong/unknown meeting
// number, registration or events link, …) — the only kind a connect form should
// blame the link input for. Everything else (bot removed, missing authorization)
// is about the meeting or the account, so the link must not be highlighted.
// The flag lives on the ZOOM_ERROR_CODES entry, but the payloads that come back
// from a join failure are hand-built from the bot's own copy of the codes and
// carry only the code, so fall back to looking the code up here.
export const isZoomLinkError = (error?: string | object | null): boolean => {
  if (!error) return false;
  const parsed: any = parseErrorMessage(error);
  if (!parsed || typeof parsed !== 'object') return false;
  if (typeof parsed.isLinkError === 'boolean') return parsed.isLinkError;
  const code = parsed.errorCode ?? parsed.code ?? parsed.status;
  if (code === undefined || code === null || code === '') return false;
  return !!ZOOM_ERROR_CODES[code]?.isLinkError;
};

const createCtxError = (ctx: TestContext, errorCode: string) => {
  const customError = getCustomZoomError({
    origin: ZOOM_VALIDATION_ERROR,
    errorCode,
    status: 400,
  });

  return ctx.createError({
    message: JSON.stringify(customError)
  });
};

export const zoomLinkValidator = (name: string = 'linkValidation') => {
  return string().test({
    name,
    test(value = '', ctx) {
      let message = '';
      if (!value) {
        message = 'Please enter your meeting link.';
      }

      if (!value && message) {
        return createCtxError(ctx, EMPTY_LINK_ERROR_CODE);
      }

      if (value?.includes('events')) {
        return createCtxError(ctx, EVENT_LINK_ERROR_CODE);
      }

      if (value?.includes('register')) {
        return createCtxError(ctx, REGISTRATION_LINK_ERROR_CODE);
      }

      const { meetingId } = zoomLinkParser(value);
      if (meetingId) return true;

      return createCtxError(ctx, INVALID_LINK_ERROR_CODE);
    }
  }).transform((value) => {
    const { joinUrl } = zoomLinkParser(value);
    if (joinUrl) return joinUrl;
    return value;
  });
};

export const getDeepLinkErrorRedirectUrl = (url: string, error: any, auth: any, ctx: string | null) => {
  const urlObj = applyAuthorizeParams(new URL(url), { userId: auth.id, saCtx: ctx });

  if (error?.status === 400 && error?.errorCode === 'ZOOM_ID_MISMATCH') {
    urlObj.pathname = '/zoom/connect';
    urlObj.searchParams.set('email', error?.zoomPlatform?.profile?.email);
    urlObj.searchParams.set('pId', error?.zoomPlatform?.id);
    return urlObj;
  } else if ([400, 401].includes(error.status)) {
    urlObj.pathname = ZOOM_PROXY_AUTH_PATH;
    return urlObj;
  }
};
